import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import ClipLoader from "react-spinners/ClipLoader";
import { useSnackbar } from "notistack";

import MetaComponent from "../../components/common/MetaComponent";
import SidebarMenu from "../../components/dashboard/SidebarMenu";
import Header2 from "../../components/headers/Header2";
import { GetEnquiryByVendorId, UpdateIsReadVendorEnquiry } from "../../APIs/EnquiryAPI";

const metadata = {
    title: "Manage Enquiry || AskMeReality - Real Estate",
    description: "AskMeReality - Real Estate",
};

const boolLabel = (value) => (value === true ? "Yes" : value === false ? "No" : "-");

const getEnquiryType = (enquiry) => {
    if (enquiry?.IsForCall === true) return " Call";
    if (enquiry?.IsForWhatsapp === true) return "Whatsapp Chat";
    if (enquiry?.IsForEnquiry === true) return "Enquiry";
    return "-";
};

const getEnquiryTypeStyle = (enquiry) => {
    if (enquiry?.IsForCall === true) {
        return {
            backgroundColor: "#dbeafe",
            color: "#1d4ed8",
            border: "1px solid #93c5fd",
        };
    }

    if (enquiry?.IsForWhatsapp === true) {
        return {
            backgroundColor: "#dcfce7",
            color: "#15803d",
            border: "1px solid #86efac",
        };
    }

    if (enquiry?.IsForEnquiry === true) {
        return {
            backgroundColor: "#fef3c7",
            color: "#b45309",
            border: "1px solid #fcd34d",
        };
    }

    return {
        backgroundColor: "#f3f4f6",
        color: "#6b7280",
        border: "1px solid #d1d5db",
    };
};

const normalizePhoneNumber = (value) => String(value || "").replace(/\D/g, "");

const buildWhatsAppUrl = (phoneNumber) => {
    const digits = normalizePhoneNumber(phoneNumber);
    if (!digits) return "";

    return `https://wa.me/${digits}`;
};

const buildCallUrl = (phoneNumber) => {
    const digits = normalizePhoneNumber(phoneNumber);
    if (!digits) return "";

    return `tel:${digits}`;
};

const getLocalDateKey = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const isToday = (value) => getLocalDateKey(value) === getLocalDateKey(new Date());

const isYesterday = (value) => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return getLocalDateKey(value) === getLocalDateKey(date);
};

const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const getStoredVendorId = () => {
    try {
        const storedId = localStorage.getItem("id");
        if (!storedId) return 0;
        const parsedId = JSON.parse(storedId);
        return parsedId || storedId || 0;
    } catch (error) {
        return localStorage.getItem("id") || 0;
    }
};

const tableStyles = {
    wrapper: {
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        overflowX: "auto",
        overflowY: "hidden",
        background: "#ffffff",
    },
    table: {
        borderCollapse: "collapse",
        width: "100%",
        minWidth: "980px",
    },
    headerRow: {
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #dbe3eb",
    },
    headerCell: {
        padding: "14px 12px",
        fontWeight: 700,
        color: "#111827",
        fontSize: "14px",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
    },
    bodyRow: {
        borderBottom: "1px solid #e5e7eb",
    },
    bodyCell: {
        padding: "14px 12px",
        verticalAlign: "middle",
        color: "#1f2937",
    },
    statusBadge: {
        cursor: "default",
        minWidth: "70px",
        textAlign: "center",
        display: "inline-block",
    },
    actionButton: {
        padding: "8px 12px",
        fontWeight: 600,
        borderRadius: "8px",
    },
};

function ManageEnquiry() {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [enquiries, setEnquiries] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedTypeFilter, setSelectedTypeFilter] = useState("all");
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [updatingEnquiryId, setUpdatingEnquiryId] = useState(null);

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const fetchEnquiries = async () => {
        try {

            const vendorId = getStoredVendorId();
            if (!vendorId || vendorId === "0") {
                enqueueSnackbar("Vendor ID not found. Please login again.", { variant: "error" });
                return;
            }

            const token = JSON.parse(localStorage.getItem("tcn"));
            if (!token) {
                enqueueSnackbar("Authentication token not found. Please login again.", { variant: "error" });
                return;
            }

            setIsLoading(true);
            const response = await GetEnquiryByVendorId(vendorId, token);

            if (!(response?.isSuccess || response?.success)) {
                enqueueSnackbar(response.message || "Failed to load enquiries.", { variant: "warning" });
            }

            setEnquiries(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Error fetching enquiries:", error);
            enqueueSnackbar("Error fetching enquiries. Please try again.", { variant: "error" });
            setEnquiries([]);
        } finally {
            setIsLoading(false);
        }
    };

    const openDetails = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setIsModalOpen(true);
    };

    const handleWhatsAppChat = (enquiry) => {
        const url = buildWhatsAppUrl(enquiry?.Mobile);
        if (!url) {
            enqueueSnackbar("Mobile number not found for WhatsApp chat.", { variant: "warning" });
            return;
        }

        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleMobileCall = (enquiry) => {
        const url = buildCallUrl(enquiry?.Mobile);
        if (!url) {
            enqueueSnackbar("Mobile number not found for calling.", { variant: "warning" });
            return;
        }

        window.location.href = url;
    };

    const syncReadStatus = async (enquiry, nextIsReadVendor) => {
        try {
            const enquiryId = enquiry?.EnquiryId;
            const vendorId = getStoredVendorId();
            const token = JSON.parse(localStorage.getItem("tcn"));

            if (!enquiryId) {
                enqueueSnackbar("Enquiry ID not found.", { variant: "warning" });
                return;
            }

            if (!vendorId || vendorId === "0") {
                enqueueSnackbar("Vendor ID not found. Please login again.", { variant: "error" });
                return;
            }

            if (!token) {
                enqueueSnackbar("Authentication token not found. Please login again.", { variant: "error" });
                return;
            }

            setUpdatingEnquiryId(enquiryId);

            const response = await UpdateIsReadVendorEnquiry(
                enquiryId,
                nextIsReadVendor ? "readed" : "unread",
                token,
            );

            if (!response.success) {
                enqueueSnackbar(response.message || "Failed to update enquiry status.", { variant: "warning" });
                return;
            }

            setEnquiries((currentItems) =>
                currentItems.map((item) =>
                    item.EnquiryId === enquiryId
                        ? { ...item, IsReadVendor: nextIsReadVendor }
                        : item
                )
            );

            setSelectedEnquiry((currentItem) =>
                currentItem && currentItem.EnquiryId === enquiryId
                    ? { ...currentItem, IsReadVendor: nextIsReadVendor }
                    : currentItem
            );

            enqueueSnackbar(
                nextIsReadVendor ? "Marked as read successfully." : "Marked as unread successfully.",
                { variant: "success" }
            );
        } catch (error) {
            console.error("Error updating enquiry status:", error);
            enqueueSnackbar("Failed to update enquiry status.", { variant: "error" });
        } finally {
            setUpdatingEnquiryId(null);
        }
    };

    const closeDetails = () => {
        setIsModalOpen(false);
        setSelectedEnquiry(null);
    };

    const filteredEnquiries = enquiries.filter((enquiry) => {
        const searchValue = searchText.trim().toLowerCase();
        if (!searchValue) return true;

        const searchableText = [
            enquiry?.PropertyGeneratedId,
            enquiry?.PropertyTitle,
            enquiry?.ProjectTypeName,
            enquiry?.PropertyLabelName,
            enquiry?.Name,
            enquiry?.Mobile,
            enquiry?.Email,
            enquiry?.Message,
            enquiry?.City,
            enquiry?.State,
            getEnquiryType(enquiry),
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        return searchableText.includes(searchValue);
    });

    const totalEnquiries = enquiries.length;
    const todayEnquiries = enquiries.filter((enquiry) => isToday(enquiry?.CreatedOn)).length;
    const yesterdayEnquiries = enquiries.filter((enquiry) => isYesterday(enquiry?.CreatedOn)).length;
    const whatsappEnquiries = enquiries.filter((enquiry) => enquiry?.IsForWhatsapp === true).length;
    const callEnquiries = enquiries.filter((enquiry) => enquiry?.IsForCall === true).length;
    const enquiryOnlyCount = enquiries.filter((enquiry) => enquiry?.IsForEnquiry === true).length;

    const bucketFilteredEnquiries = filteredEnquiries.filter((enquiry) => {
        if (selectedTypeFilter === "all") return true;
        if (selectedTypeFilter === "today") return isToday(enquiry?.CreatedOn);
        if (selectedTypeFilter === "yesterday") return isYesterday(enquiry?.CreatedOn);
        if (selectedTypeFilter === "whatsapp") return enquiry?.IsForWhatsapp === true;
        if (selectedTypeFilter === "call") return enquiry?.IsForCall === true;
        if (selectedTypeFilter === "enquiry") return enquiry?.IsForEnquiry === true;
        return true;
    }).filter((enquiry) => {
        if (!selectedDate) return true;
        return getLocalDateKey(enquiry?.CreatedOn) === selectedDate;
    });

    const totalFilteredItems = bucketFilteredEnquiries.length;
    const totalPages = Math.max(1, Math.ceil(totalFilteredItems / itemsPerPage));
    const safePage = Math.min(page, totalPages);
    const startIndex = (safePage - 1) * itemsPerPage;
    const paginatedEnquiries = bucketFilteredEnquiries.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => {
        setPage(1);
    }, [searchText, itemsPerPage, selectedTypeFilter, selectedDate]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const getPageNumbers = () => {
        const maxVisiblePages = 5;
        const pages = [];
        let start = Math.max(1, safePage - 2);
        let end = Math.min(totalPages, start + maxVisiblePages - 1);

        start = Math.max(1, end - maxVisiblePages + 1);
        for (let index = start; index <= end; index += 1) {
            pages.push(index);
        }
        return pages;
    };

    const filterCards = [
        { key: "all", title: "Total Enquiry", count: totalEnquiries, color: "#2563eb" },
        { key: "today", title: "Today Enquiry", count: todayEnquiries, color: "#0f766e" },
        { key: "yesterday", title: "Yesterday Enquiry", count: yesterdayEnquiries, color: "#7c3aed" },
        { key: "whatsapp", title: "Whatsapp Chat", count: whatsappEnquiries, color: "#16a34a" },
        { key: "call", title: "For Call", count: callEnquiries, color: "#1d4ed8" },
        { key: "enquiry", title: "For Enquiry", count: enquiryOnlyCount, color: "#d97706" },
    ];

    const detailItems = selectedEnquiry
        ? [

           // { label: "Enquiry Type", value: getEnquiryType(selectedEnquiry) },
            { label: "Name", value: selectedEnquiry.Name },
            { label: "Mobile", value: selectedEnquiry.Mobile },
            { label: "Email", value: selectedEnquiry.Email },
            { label: "Message", value: selectedEnquiry.Message },
            // { label: "Property Title", value: selectedEnquiry.PropertyTitle },
            // { label: "Property Generated Id", value: selectedEnquiry.PropertyGeneratedId },
            // { label: "Address", value: selectedEnquiry.Address },
            // { label: "City", value: selectedEnquiry.City },
            // { label: "State", value: selectedEnquiry.State },
            // { label: "Latitude", value: selectedEnquiry.Latitude },
            // { label: "Longitude", value: selectedEnquiry.Longitude },          
            // { label: "Project Type Name", value: selectedEnquiry.ProjectTypeName },
            // { label: "Property Label Name", value: selectedEnquiry.PropertyLabelName },          
        ]
        : [];

    return (
        <>
            <MetaComponent meta={metadata} />
            <div className="layout-wrap">
                <Header2 />
                <SidebarMenu />
                <div className="main-content">
                    <div className="main-content-inner">
                        <div className="button-show-hide show-mb">
                            <span className="body-1">Show Dashboard</span>
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                gap: "14px",
                                marginBottom: "18px",
                            }}
                        >
                            {filterCards.map((card) => {
                                const isActive = selectedTypeFilter === card.key;
                                return (
                                    <button
                                        key={card.key}
                                        type="button"
                                        onClick={() => setSelectedTypeFilter(card.key)}
                                        style={{
                                            border: `1px solid ${isActive ? card.color : "#e5e7eb"}`,
                                            background: isActive ? `${card.color}10` : "#ffffff",
                                            borderRadius: "16px",
                                            padding: "16px",
                                            textAlign: "left",
                                            boxShadow: isActive ? "0 10px 24px rgba(15, 23, 42, 0.08)" : "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <div style={{ fontSize: "13px", fontWeight: 700, color: "#6b7280", marginBottom: "8px" }}>
                                            {card.title}
                                        </div>
                                        <div style={{ fontSize: "28px", fontWeight: 800, color: card.color, lineHeight: 1 }}>
                                            {card.count}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="widget-box-2 wd-listing">
                            <h5 className="title">Manage Enquiry</h5>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: "12px",
                                    marginBottom: "14px",
                                }}
                            >
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by property, name, phone, email, city..."
                                    value={searchText}
                                    onChange={(event) => setSearchText(event.target.value)}
                                    style={{ minWidth: "260px", maxWidth: "450px", height: "42px" }}
                                />

                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <label htmlFor="itemsPerPage" style={{ marginBottom: 0, fontWeight: 600 }}>
                                            Rows per page:
                                        </label>
                                        <select
                                            id="itemsPerPage"
                                            className="form-control"
                                            value={itemsPerPage}
                                            onChange={(event) => setItemsPerPage(Number(event.target.value))}
                                            style={{ width: "90px", height: "42px", padding: "6px 10px" }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={30}>30</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                        <label htmlFor="selectedDate" style={{ marginBottom: 0, fontWeight: 600 }}>
                                            Date filter:
                                        </label>
                                        <input
                                            id="selectedDate"
                                            type="date"
                                            className="form-control"
                                            value={selectedDate}
                                            onChange={(event) => setSelectedDate(event.target.value)}
                                            style={{ width: "170px", height: "42px" }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light"
                                            onClick={() => setSelectedDate("")}
                                            disabled={!selectedDate}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive" style={tableStyles.wrapper}>
                                <table style={tableStyles.table}>
                                    <thead>
                                        <tr style={tableStyles.headerRow}>
                                            <th style={{ ...tableStyles.headerCell, width: "8%" }}>Sr No</th>
                                            <th style={tableStyles.headerCell}>Property</th>
                                            <th style={tableStyles.headerCell}>Contact Details</th>
                                            <th style={tableStyles.headerCell}>Enquiry For</th>
                                            <th style={tableStyles.headerCell}>Created On</th>
                                            <th style={tableStyles.headerCell}>Status</th>
                                            <th style={{ ...tableStyles.headerCell, width: "12%" }}>Action</th>
                                            <th style={{ ...tableStyles.headerCell, width: "16%" }}>Whatsapp / Call</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr style={tableStyles.bodyRow}>
                                                <td colSpan={8} style={{ ...tableStyles.bodyCell, textAlign: "center", padding: "24px 12px" }}>
                                                    <ClipLoader size={18} color="currentColor" />
                                                </td>
                                            </tr>
                                        ) : paginatedEnquiries.length > 0 ? (
                                            paginatedEnquiries.map((enquiry, index) => (
                                                <tr
                                                    className="file-delete"
                                                    key={enquiry.EnquiryId || index}
                                                    style={{
                                                        ...tableStyles.bodyRow,
                                                        backgroundColor: enquiry.IsReadVendor ? "#e2e2e2" : "#ffffff",
                                                    }}
                                                >
                                                    <td style={{ ...tableStyles.bodyCell, width: "8%" }}>
                                                        <span style={{ marginLeft: "20px" }}>{startIndex + index + 1}</span>
                                                    </td>
                                                    <td style={tableStyles.bodyCell}>
                                                        <strong style={{ fontWeight: "700" }}>{enquiry.PropertyGeneratedId}</strong><br />
                                                        <strong>{enquiry.PropertyTitle}</strong>
                                                        <div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
                                                            {enquiry.City || enquiry.State ? `${enquiry.City || ""}${enquiry.City && enquiry.State ? ", " : ""}${enquiry.State || ""}` : ""}
                                                        </div>
                                                    </td>
                                                    <td style={tableStyles.bodyCell}>
                                                        <div><strong>Name:</strong> {enquiry.Name || "-"}</div>
                                                        <div><strong>Phone:</strong> {enquiry.Mobile || "-"}</div>
                                                        <div><strong>Email:</strong> {enquiry.Email || "-"}</div>
                                                    </td>
                                                    <td style={tableStyles.bodyCell}>
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                minWidth: "140px",
                                                                padding: "8px 12px",
                                                                borderRadius: "999px",
                                                                fontSize: "12px",
                                                                fontWeight: 700,
                                                                lineHeight: 1,
                                                                whiteSpace: "nowrap",
                                                                ...getEnquiryTypeStyle(enquiry),
                                                            }}
                                                        >
                                                            {getEnquiryType(enquiry)}
                                                        </span>
                                                    </td>
                                                    <td style={tableStyles.bodyCell}>{formatDateTime(enquiry.CreatedOn)}</td>
                                                    <td style={tableStyles.bodyCell}>
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm ${enquiry.IsReadVendor ? "btn-success" : "btn-warning"}`}
                                                            style={{
                                                                ...tableStyles.statusBadge,
                                                                border: "none",
                                                                opacity: updatingEnquiryId === enquiry.EnquiryId ? 0.75 : 1,
                                                            }}
                                                            disabled={updatingEnquiryId === enquiry.EnquiryId}
                                                            onClick={() => syncReadStatus(enquiry, !enquiry.IsReadVendor)}
                                                        >
                                                            {updatingEnquiryId === enquiry.EnquiryId
                                                                ? "Updating..."
                                                                : enquiry.IsReadVendor
                                                                    ? "Read"
                                                                    : "Unread"}
                                                        </button>
                                                    </td>
                                                    <td style={tableStyles.bodyCell}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary"
                                                            style={tableStyles.actionButton}
                                                            onClick={() => {
                                                                openDetails(enquiry);
                                                                if (!enquiry.IsReadVendor) {
                                                                    syncReadStatus(enquiry, true);
                                                                }
                                                            }}
                                                        >
                                                            View Details
                                                        </button>
                                                    </td>
                                                    <td style={tableStyles.bodyCell}>
                                                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-success"
                                                                style={tableStyles.actionButton}
                                                                onClick={() => handleWhatsAppChat(enquiry)}
                                                                disabled={!normalizePhoneNumber(enquiry?.Mobile)}
                                                            >
                                                                WhatsApp
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-dark"
                                                                style={tableStyles.actionButton}
                                                                onClick={() => handleMobileCall(enquiry)}
                                                                disabled={!normalizePhoneNumber(enquiry?.Mobile)}
                                                            >
                                                                Call
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr style={tableStyles.bodyRow}>
                                                <td colSpan={8} style={{ ...tableStyles.bodyCell, textAlign: "center", padding: "24px 12px" }}>
                                                    No enquiry records found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {!isLoading && totalFilteredItems > 0 && (
                                <div
                                    style={{
                                        marginTop: "14px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: "10px",
                                    }}
                                >
                                    <div style={{ fontSize: "13px", color: "#4b5563", fontWeight: 600 }}>
                                        Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalFilteredItems)} of {totalFilteredItems} enquiries
                                    </div>

                                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light"
                                            disabled={safePage === 1}
                                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                        >
                                            Prev
                                        </button>

                                        {getPageNumbers().map((pageNo) => (
                                            <button
                                                type="button"
                                                key={pageNo}
                                                className={`btn btn-sm ${safePage === pageNo ? "btn-primary" : "btn-light"}`}
                                                onClick={() => setPage(pageNo)}
                                            >
                                                {pageNo}
                                            </button>
                                        ))}

                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light"
                                            disabled={safePage === totalPages}
                                            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="overlay-dashboard" />
            </div>

            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeDetails}
                closeTimeoutMS={250}
                style={{
                    overlay: {
                        backgroundColor: "rgba(0, 0, 0, 0.55)",
                        zIndex: 1200,
                    },
                }}
                className={{
                    base: "amr-enquiry-modal-content",
                    afterOpen: "amr-enquiry-modal-content-open",
                    beforeClose: "amr-enquiry-modal-content-close",
                }}
                overlayClassName={{
                    base: "amr-enquiry-modal-overlay",
                    afterOpen: "amr-enquiry-modal-overlay-open",
                    beforeClose: "amr-enquiry-modal-overlay-close",
                }}
                ariaHideApp={false}
            >
                <div className="amr-enquiry-shell" style={{ maxWidth: "100%" }}>
                    <button
                        type="button"
                        className="amr-enquiry-close"
                        aria-label="Close enquiry details"
                        onClick={closeDetails}
                    >
                        <span>×</span>
                    </button>

                    <div className="amr-enquiry-brand">
                        <h4 style={{ margin: 0 }}>Enquiry Details</h4>
                    </div>

                    <p className="amr-enquiry-subtitle" style={{ marginBottom: "18px" }}>
                        Review the enquiry and property information submitted by the user.
                    </p>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: "12px",
                        }}
                    >
                        {detailItems.map((item) => (
                            <div
                                key={item.label}
                                style={{
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    borderRadius: "12px",
                                    padding: "12px 14px",
                                    background: "#fff",
                                }}
                            >
                                <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: 600, marginBottom: "6px" }}>
                                    {item.label}
                                </div>
                                <div style={{ fontSize: "14px", color: "#111827", wordBreak: "break-word" }}>
                                    {item.value !== null && item.value !== undefined && item.value !== "" ? item.value.toString() : "-"}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* <button type="button" className="amr-enquiry-later" onClick={closeDetails}>
                        Close
                    </button> */}
                </div>
            </Modal>
        </>
    );
}

export default ManageEnquiry;
